import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/crm/teams")({
  component: TeamsPage,
});

import { useState, useEffect, useMemo } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { TeamTable } from "@/components/crm/tables/TeamTable";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import { TeamFormSheet } from "@/components/crm/forms/TeamFormSheet";
import {
  useFetchTeams,
  useTeamMutations,
  type TeamOrdering,
} from "@/hooks/api/crm/useTeams";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import type { CrmTeam, CrmTeamPayload } from "@/types/crm";
import { useCrmPermissions } from "@/lib/crm-permissions";

function TeamsPage() {
  // TeamViewSet: read_permission = None (open, queryset scoped to the
  // caller's own teams without it), write_permission = "crm.team.manage"
  // (create/update/delete all gated flat, no ownership fallback).
  const { canManageTeams } = useCrmPermissions();
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        name: SortDirection;
        is_active: SortDirection;
        created_at: SortDirection;
      },
      TeamOrdering
    >({ name: null, is_active: null, created_at: null });

  const teamsQuery = useFetchTeams({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    ordering: getApiOrdering(),
  });
  const teams = teamsQuery.data?.results ?? [];
  const count = teamsQuery.data?.count ?? 0;

  const { createTeam, updateTeam, deleteTeam } = useTeamMutations();

  const isMutating =
    createTeam.isPending || updateTeam.isPending || deleteTeam.isPending;
  const isLoading = teamsQuery.isPending || isMutating;
  const isFetching = teamsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, setPage]);

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<CrmTeam | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<CrmTeam | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setOrdering({ name: null, is_active: null, created_at: null });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmTeamPayload) => {
    const team = await createTeam.mutateAsync(data);
    toast.success("Team created!");
    return team;
  };
  const onEdit = async (id: number, data: Partial<CrmTeamPayload>) => {
    await updateTeam.mutateAsync({ id, payload: data });
    toast.success("Team updated!");
    setIsSheetOpen(false);
  };
  const handleDeleteConfirm = async () => {
    if (!teamToDelete) return;
    try {
      await deleteTeam.mutateAsync({ id: teamToDelete.id });
      toast.success("Team deleted!");
      setIsDeleteDialogOpen(false);
      setTeamToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="CRM Teams"
          description="Manage CRM teams."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={
            canManageTeams
              ? () => {
                  setEditingTeam(null);
                  setIsViewOnly(false);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Add Team"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search teams..."
          />
        </FiltersPanel>
        <TeamTable
          teams={teams}
          onView={(t) => {
            setEditingTeam(t);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(t) => {
            setEditingTeam(t);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(t) => {
            setTeamToDelete(t);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <TeamFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingTeam={editingTeam}
          isLoading={isMutating}
        />
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Team"
          description={`Delete team "${teamToDelete?.name}"?`}
          itemName={teamToDelete?.name}
          isLoading={isMutating}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
